import { DatePicker, Radio } from '@maxtropy/components';
import {
  apiV2EnergyReportTotalOverviewPost,
  V2EnergyReportTotalOverviewPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { RadioChangeEvent, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { createContext, useEffect, useMemo, useState } from 'react';
import { configuredListData, gasConfigList, OverviewDateType, OverviewDateTypeDisplay } from '../../const';
import { getDefaultTime } from '../../utils';
import CardBox from '../CardBox';
import AirCard from './components/AirCard';
import AirSteamBox from './components/AirSteamBox';
import CostRateBox from './components/CostRateBox';
import ElecCard from './components/ElecCard';
import ElecWaterBox from './components/ElecWaterBox';
import WaterSteamCard from './components/WaterSteamCard';
import styles from './index.module.scss';
import { GasType, OverviewFieldToGasType } from '../Air/useGetGasData';

export interface ShareDataWrapperProps {
  overviewData?: V2EnergyReportTotalOverviewPostResponse;
  loading?: boolean;
  timeResolution?: OverviewDateType;
}

export const ShareDataWrapperContext = createContext<ShareDataWrapperProps>({});

// 报表范围类型（与父组件保持一致）
enum ReportScope {
  FACTORY = 'factory',
  DEPT = 'dept',
  AREA = 'area',
}

type overviewProp = {
  setHasConfigGas: (gasList: GasType[]) => void;
  onDateChange?: (dates: [Dayjs | null, Dayjs | null]) => void;
  reportScope?: ReportScope;
  selectedDeptAreaId?: number;
};
const Overview = ({ setHasConfigGas, onDateChange, reportScope, selectedDeptAreaId }: overviewProp) => {
  const [displayValue, setDisplayValue] = useState<Dayjs | undefined>(dayjs()); // 日期选择器默认选择当天
  const [timeResolution, setTimeResolution] = useState<OverviewDateType>(OverviewDateType.YEAR); // 选择时间颗粒度
  // 总览
  const { data: overviewData, loading } = useRequest(
    async () => {
      const params: any = {
        fromTime: getDefaultTime(timeResolution!, displayValue).startTime,
        toTime: getDefaultTime(timeResolution!, displayValue).endTime,
      };
      // 根据报表范围传递参数
      if (reportScope === ReportScope.DEPT) {
        params.deptOrArea = 1;
        params.deptAreaId = selectedDeptAreaId;
      } else if (reportScope === ReportScope.AREA) {
        params.deptOrArea = 2;
        params.deptAreaId = selectedDeptAreaId;
      }
      // 总览模块不传递月周期参数，按用户选择的时间范围查询
      return await apiV2EnergyReportTotalOverviewPost(params);
    },
    {
      ready: !!timeResolution && !!displayValue,
      refreshDeps: [timeResolution, displayValue, reportScope, selectedDeptAreaId],
    }
  );

  useEffect(() => {
    const gasInfo = overviewData?.gasInfo;
    const configuredList = gasConfigList
      .filter(config => {
        const gasData = gasInfo?.[config.key as keyof typeof gasInfo] as configuredListData;
        return gasData && gasData.hasConfig === true;
      })
      .map(config => {
        return OverviewFieldToGasType[config.key];
      });
    setHasConfigGas(configuredList);
  }, [overviewData, setHasConfigGas]);

  const disabledDate = (current: Dayjs) => {
    return current && current >= dayjs().endOf('day');
  };

  // render picker
  const picker = useMemo(() => {
    if (timeResolution === OverviewDateType.MONTH) {
      return 'month';
    } else {
      return 'year';
    }
  }, [timeResolution]);

  const onRadioChange = (e: RadioChangeEvent) => {
    const value: OverviewDateType = e.target.value;
    setTimeResolution(value);
    const newDate = dayjs();
    setDisplayValue(newDate);

    // 切换时间维度时，也更新父组件的趋势图
    if (!onDateChange) return;

    let startDate: Dayjs;
    let endDate: Dayjs;

    if (value === OverviewDateType.YEAR) {
      // 选择年：显示该年的按天维度数据，截止到当前时间
      startDate = newDate.startOf('year');
      endDate = dayjs().endOf('day');
    } else {
      // 选择月：显示该月的按天维度数据，截止到当前时间
      startDate = newDate.startOf('month');
      endDate = dayjs().endOf('day');
    }
    onDateChange([startDate, endDate]);
  };

  const onDatePickerChange = (date: Dayjs | null) => {
    if (!date) return;
    setDisplayValue(date);

    // 根据选择的时间维度，计算日期范围并通知父组件
    if (!onDateChange) return;

    let startDate: Dayjs;
    let endDate: Dayjs;
    const now = dayjs();

    if (timeResolution === OverviewDateType.YEAR) {
      // 选择年：显示该年的按天维度数据
      startDate = date.startOf('year');
      // 如果是今年，截止到当前时间；否则显示完整年份
      endDate = date.year() === now.year() ? now.endOf('day') : date.endOf('year');
    } else {
      // 选择月：显示该月的按天维度数据
      startDate = date.startOf('month');
      // 如果是今月，截止到当前时间；否则显示完整月份
      endDate = date.year() === now.year() && date.month() === now.month() ? now.endOf('day') : date.endOf('month');
    }
    onDateChange([startDate, endDate]);
  };

  return (
    <div className={styles.overview_wrapper}>
      <CardBox title="总览">
        {/* 月年和时间选择 - 绝对定位紧跟标题 */}
        <div className={styles.date_selector}>
          <Space size={16}>
            <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
              <Radio.Button value={OverviewDateType.MONTH}>
                {OverviewDateTypeDisplay[OverviewDateType.MONTH]}
              </Radio.Button>
              <Radio.Button value={OverviewDateType.YEAR}>
                {OverviewDateTypeDisplay[OverviewDateType.YEAR]}
              </Radio.Button>
            </Radio.Group>
            <div>
              <DatePicker
                style={{ width: '250px' }}
                picker={picker}
                value={displayValue}
                disabledDate={disabledDate}
                onChange={onDatePickerChange}
                allowClear={false}
              />
            </div>
          </Space>
        </div>
        <ShareDataWrapperContext.Provider value={{ overviewData, loading, timeResolution }}>
          <div className={styles.content_wrapper}>
            <div className={styles.item_electricity}>
              {/* 用电量统计 */}
              <ElecCard showPhotovoltaic={reportScope === ReportScope.FACTORY} />
            </div>
            <div className={styles.content_divider}></div>
            <div className={styles.item_water_gas}>
              {/* 用水量统计 */}
              <WaterSteamCard />
              {/* 用气量统计 */}
              <AirCard />
            </div>
          </div>
          <div className={styles.cost_content_wrapper}>
            <div className={styles.top_title}>
              <div className={styles.center_line_box_dash}></div>
              <div className={styles.center_line_box_text}>成本统计</div>
              <div className={styles.center_line_box_dash}></div>
            </div>
            <div className={styles.bottom_content_wrapper}>
              <div className={styles.item_rate} style={{ width: 280, flex: 'unset' }}>
                <CostRateBox showPlanCost={reportScope === ReportScope.FACTORY} />
              </div>
              <div className={styles.item_rate}>
                <ElecWaterBox />
              </div>
              <div className={styles.item_rate}>
                <AirSteamBox />
              </div>
            </div>
          </div>
        </ShareDataWrapperContext.Provider>
      </CardBox>
    </div>
  );
};

export default Overview;
